import React, { Fragment } from "react";

import { Calendar as CalendarIcon } from "react-feather";
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { parseISO } from "date-fns";

import { DobPicker, SalutationButtons } from "@hubcapp/components";

import { PersonalDetailsProps } from "./types";
import { PersonalDetailsSkeleton } from "./components";

const useStyles = makeStyles(theme => ({
  header: {
    display: "flex",
    justifyContent: "space-between",
  },
  sectionTitle: {
    margin: theme.spacing(0, 0, 2, 0),
  },
}));

const PersonalDetails: React.FC<PersonalDetailsProps> = ({
  personId,
  name,
  dateOfBirth,
  address,
  emailAddress,
  mobileNumber = "",
  landlineNumber = "",
  loading = false,
}) => {
  const classes = useStyles();
  const lastAddress = address?.[0];
  const { line1, line2, town, county, postcode } = lastAddress ?? {};

  return (
    <Card data-test="personal-details-card">
      <CardHeader
        classes={{ content: classes.header }}
        title="Personal details"
        subheader={`Instructor ref no. ${personId}`}
        subheaderTypographyProps={{
          component: "span",
          variant: "caption",
          ["data-test"]: "personal-details-reference",
        }}
      />
      <Divider />
      <CardContent>
        {loading ? (
          <PersonalDetailsSkeleton />
        ) : (
          <Grid container spacing={5}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                color="textSecondary"
                className={classes.sectionTitle}
                data-test="personal-details-sub-header"
              >
                My name & address
              </Typography>

              {name?.title && (
                <SalutationButtons disabled value={name?.title} />
              )}

              <TextField
                disabled
                fullWidth
                margin="normal"
                label="First name"
                name="firstName"
                value={name?.firstName}
                variant="outlined"
                inputProps={{
                  ["data-test"]: "personal-details-first-name-input",
                }}
              />

              <TextField
                disabled
                fullWidth
                margin="normal"
                label="Last name"
                name="lastName"
                value={name?.lastName}
                variant="outlined"
                inputProps={{
                  ["data-test"]: "personal-details-last-name-input",
                }}
              />

              <DobPicker
                disabled
                margin="normal"
                format="dd/MM/yyyy"
                keyboardIcon={<CalendarIcon />}
                label="Date of birth"
                name="dateOfBirth"
                value={
                  typeof dateOfBirth === "string"
                    ? parseISO(dateOfBirth)
                    : dateOfBirth
                }
                inputProps={{
                  ["data-test"]: "personal-details-dob-input",
                }}
              />
              {lastAddress && (
                <Fragment>
                  <TextField
                    disabled
                    fullWidth
                    margin="normal"
                    label="Address"
                    name="address"
                    value={[line1, line2, town, county, postcode]
                      .filter(Boolean)
                      .join(", ")}
                    variant="outlined"
                    inputProps={{
                      ["data-test"]: "personal-details-address-input",
                    }}
                  />

                  <TextField
                    disabled
                    fullWidth
                    margin="normal"
                    label="Postcode"
                    name="postcode"
                    value={postcode}
                    variant="outlined"
                    inputProps={{
                      ["data-test"]: "personal-details-postcode-input",
                    }}
                  />
                </Fragment>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                color="textSecondary"
                className={classes.sectionTitle}
                data-test="personal-details-sub-header"
              >
                Email and telephone
              </Typography>

              <TextField
                disabled
                fullWidth
                margin="normal"
                label="Email address"
                type="email"
                name="emailAddress"
                value={emailAddress}
                variant="outlined"
                inputProps={{
                  ["data-test"]: "personal-details-email-address-input",
                }}
              />

              <TextField
                disabled
                fullWidth
                margin="normal"
                label="Mobile number"
                name="mobileNumber"
                value={mobileNumber}
                variant="outlined"
                inputProps={{
                  ["data-test"]: "personal-details-mobile-number-input",
                }}
              />

              <TextField
                disabled
                fullWidth
                margin="normal"
                label="Landline number"
                name="landlineNumber"
                value={landlineNumber}
                variant="outlined"
                inputProps={{
                  ["data-test"]: "personal-details-landline-number-input",
                }}
              />
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default PersonalDetails;
